import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Hash, Loader2 } from 'lucide-react';
import { GroupBadge } from './GroupBadge';
import { groupService } from '@/lib/api/group.service';
import type { GuestGroup } from '@/types/group.types';

interface GroupManagerProps {
  celebrationId: number;
  onFilterByGroup: (guestIds: number[] | null, groupQuery: string) => void;
  activeGroupQuery: string;
  onManageMembers?: (groupId: number, groupName: string) => void;
  managingGroupId?: number | null;
}

export const GroupManager: React.FC<GroupManagerProps> = ({
  celebrationId,
  onFilterByGroup,
  activeGroupQuery,
}) => {
  const { t } = useTranslation('guests');
  const [groups, setGroups] = useState<GuestGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadGroups = useCallback(async () => {
    if (!celebrationId) return;
    setLoading(true);
    try {
      const data = await groupService.getGroups(celebrationId);
      setGroups(data);
      setError(null);
    } catch {
      setError(t('groupManager.errors.loadGroups'));
    } finally {
      setLoading(false);
    }
  }, [celebrationId]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const handleGroupClick = async (group: GuestGroup) => {
    const query = `#${group.name}`;
    if (activeGroupQuery === query) {
      // Toggle off
      onFilterByGroup(null, '');
      return;
    }
    try {
      const result = await groupService.searchByGroups(celebrationId, query);
      onFilterByGroup(result.guest_ids, query);
    } catch {
      setError(t('groupManager.errors.filterGroup'));
    }
  };

  const systemGroups = groups.filter(g => g.is_system_group);
  const customGroups = groups.filter(g => !g.is_system_group);

  if (loading && groups.length === 0) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground p-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t('groupManager.loading')}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {error && (
        <div className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded flex items-center justify-between">
          {error}
          <button onClick={() => setError(null)} className="ml-2 text-red-400 hover:text-red-600" title={t('groupManager.dismissError')}>&times;</button>
        </div>
      )}

      {/* System Groups */}
      <div>
        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 flex items-center gap-1">
          <Users className="h-3 w-3" /> {t('groupManager.systemGroups')}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {systemGroups.map(group => (
            <GroupBadge
              key={group.id}
              name={group.name}
              color={group.color}
              isSystem
              count={group.member_count}
              active={activeGroupQuery === `#${group.name}`}
              onClick={() => handleGroupClick(group)}
            />
          ))}
        </div>
      </div>

      {/* Custom Groups */}
      <div>
        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 flex items-center gap-1">
          <Hash className="h-3 w-3" /> {t('groupManager.customGroups')}
        </div>

        {customGroups.length === 0 && (
          <p className="text-xs text-muted-foreground italic">{t('groupManager.noCustomGroups')}</p>
        )}

        <div className="flex flex-wrap gap-1.5">
          {customGroups.map(group => (
            <GroupBadge
              key={group.id}
              name={group.name}
              color={group.color}
              count={group.member_count}
              active={activeGroupQuery === `#${group.name}`}
              onClick={() => handleGroupClick(group)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
